import React, { useEffect, useState } from 'react';
import { useLoadingScreen } from '../../../../hooks/useLoadingScreen';
import { UserManagementModifyUserRoute, UserManagementRoute } from '../../../../routes/routes';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { IUsersState } from '../../../../store/users/store';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../../store/store';
import { clearUsersError, fetchUsers, patchUser, putUser } from '../../../../store/users/actions';
import { ExtendedUser } from '../../../../store/users/types';
import { usePathname } from '../../../../hooks/usePathname';
import style from './UserManagementModify.module.scss';
import { ButtonPrimary, Checkbox, Input } from '@skf/bt-wind-rail-commonlibrary';
import { useTranslation } from 'react-i18next';
import { isObjectEqual } from '../../../../services/objectService';
import { useToast } from '../../../../hooks/useToast';
import { isValidResponse } from '../../../../services/responseService';
import { isAlphaNumeric } from '../../../../services/stringServices';

interface IUserManagementModifyProps {}

const UserManagementModify: React.FunctionComponent<IUserManagementModifyProps> = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoadingScreen();
  const { setPathname } = usePathname();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { params } = useRouteParams<{ username?: string }>(UserManagementModifyUserRoute);

  const { users, status, message }: IUsersState = useSelector<StoreState, IUsersState>((state) =>  state.users);

  const [oldUser, setOldUser] = useState<ExtendedUser | undefined>();
  const [user, setUser] = useState<ExtendedUser | undefined>();
  const [userRequest, setUserRequest] = useState<{ put: boolean, patch: boolean }>();

  useEffect(() => {
    if(users === undefined) {
      dispatch(fetchUsers());
      return;
    }

    if(!params?.username) return;
    const _oldUser = users.find(_user => params.username === _user.userName);
    setUser(_oldUser);

    if(!oldUser)
      setOldUser(_oldUser);

    setLoading(false);
    dispatch(clearUsersError());
  }, [users, params]);

  useEffect(() => {
    if(!params?.username) return;
    if(!user) return setPathname(UserManagementRoute.path);

    setLoading(false);
  }, [user]);

  /**
   * On request
   */
  useEffect(() => {
    if(!userRequest || status) return;

    const { put, patch } = userRequest;

    if(patch)
      dispatch(patchUser(user));

    if(put)
      dispatch(putUser(user));

    if(patch || put)
      setLoading(true);

    setOldUser(user);
  }, [userRequest]);

  /**
   * On response
   */
  useEffect(() => {
    if(!userRequest || !message || !status) return;

    const _isValidResponse = isValidResponse(status);

    let _message = message;

    if(message === 'put')
      _message = t('User_updated_successfully');

    if(message === 'patch')
      _message = t('User_disabled_enabled_successfully');

    addToast({
      message: _message,
      status: _isValidResponse ? 'ok' : 'danger'
    });

    setLoading(false);
    dispatch(clearUsersError());
    setPathname(UserManagementRoute.path);
  }, [userRequest, status, message]);

  const onClickIsDisabled = () => {
    setUser({
      ...(user as ExtendedUser),
      isEnabled: !user?.isEnabled
    });
  };

  // const onClickIsSKF = () => {
  //   setUser({
  //     ...(user as ExtendedUser),
  //     isSKF: !user?.isSKF
  //   });
  // };

  const onChangeGlobalId = (value: string) => {
    setUser({
      ...(user as ExtendedUser),
      globalId: value
    });
  };

  const onClickCancel = () => {
    setPathname(UserManagementRoute.path);
  };

  const onClickSubmit = () => {
    const isDisabledChanged = oldUser?.isEnabled !== user?.isEnabled;
    const isDataChanged = oldUser?.isSKF !== user?.isSKF || oldUser?.globalId !== user?.globalId;

    const errorList: string[] = [];

    // check if globalId is already in use
    const isGlobalId = users?.some(_user =>
      _user.userName !== user?.userName
      && _user.globalId === user?.globalId
    );
    if(user?.globalId && !isAlphaNumeric(user.globalId))
      errorList.push(t('Global_Id_cannot_contain_non_alphanumeric_characters'));

    if((user?.globalId?.length || 0) > 0 && isGlobalId)
      errorList.push(t('Already_GlobalId'));

    errorList.forEach(_message => addToast({ status: 'danger', message: t(_message) }));
    if(errorList.length > 0) return;

    setUserRequest({
      put: isDataChanged,
      patch: isDisabledChanged
    });
  };

  return (
    <div className={style.content}>
      <div className={style.category}>
        <div className={style.userName}>
          {user?.userName}
        </div>
      </div>

      <div className={style.category}>
        <div className={style.inputRowContainer}>
          <div className={style.checkbox}>
            <Checkbox
              square
              isChecked={!user?.isEnabled}
              onClick={onClickIsDisabled}
            >
              {t('Is_Disabled')}
            </Checkbox>
          </div>
          <div className={style.checkbox}>
            <Checkbox
              square
              isChecked={user?.isSKF}
              isDisabled={true}
              // onClick={onClickIsSKF}
              leftLabel
            >
              {t('Is_SKF')}
            </Checkbox>
          </div>
          <Input
            placeholder={t('Global_Id')}
            value={user?.globalId}
            disabled={!user?.isEnabled || oldUser?.isSKF}
            onChange={onChangeGlobalId}
          />
        </div>
      </div>
      <div className={style.navigation}>
        <ButtonPrimary
          onClick={onClickCancel}
          outline
        >
          {t('Cancel')}
        </ButtonPrimary>
        <ButtonPrimary
          disabled={isObjectEqual(oldUser, user)}
          onClick={onClickSubmit}
        >
          {t('Save')}
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default UserManagementModify;
